import React from 'react';
import HelpArticle from '../../components/help/HelpArticle';

const ExtendRental: React.FC = () => {
  const content = (
    <>
      <h2>Forlæng din leje</h2>
      <p>
        Har du brug for værktøjet længere end planlagt? Her er hvordan du forlænger din lejeperiode.
      </p>

      <h3>Hvornår kan du forlænge?</h3>
      <p>
        Du kan forlænge din leje hvis:
      </p>
      <ul>
        <li>Værktøjet ikke er booket af andre efter din periode</li>
        <li>Ejeren accepterer forlængelsen</li>
        <li>Du kontakter ejeren i god tid</li>
      </ul>

      <h3>Kontakt ejeren tidligt</h3>
      <p>
        Jo tidligere du spørger, jo bedre:
      </p>
      <ul>
        <li><strong>Ideelt:</strong> Mindst 2 dage før returnering</li>
        <li><strong>Acceptabelt:</strong> Minimum 1 dag før</li>
        <li><strong>Undgå:</strong> At vente til returdagen</li>
      </ul>
      <p>
        Tidlig kommunikation viser respekt og giver ejeren tid til at planlægge.
      </p>

      <h3>Sådan anmoder du om forlængelse</h3>
      <p>
        Følg disse trin:
      </p>

      <h4>Trin 1: Åbn din booking</h4>
      <ul>
        <li>Gå til "Mine bookinger" i din profil</li>
        <li>Find den aktive booking</li>
        <li>Klik for at se detaljer</li>
      </ul>

      <h4>Trin 2: Send besked til ejeren</h4>
      <p>
        Brug chatfunktionen til at skrive en besked som:
      </p>
      <blockquote>
        "Hej [Navn], jeg har brug for [værktøjsnavn] lidt længere end planlagt. Vil det være muligt at forlænge lejen til [ny dato]? Jeg betaler selvfølgelig for de ekstra dage."
      </blockquote>

      <h4>Trin 3: Vent på svar</h4>
      <p>
        Ejeren vil tjekke deres kalender og svare dig. De vil sandsynligvis:
      </p>
      <ul>
        <li>Acceptere hvis værktøjet er ledigt</li>
        <li>Foreslå et alternativt slutdato</li>
        <li>Afvise hvis det er booket</li>
      </ul>

      <h4>Trin 4: Bekræft detaljer</h4>
      <p>
        Hvis ejeren accepterer, aftal:
      </p>
      <ul>
        <li>Ny returdato og tid</li>
        <li>Pris for ekstra dage</li>
        <li>Hvordan du betaler for forlængelsen</li>
        <li>Om du skal mødes igen eller bare returnere senere</li>
      </ul>

      <h3>Betaling for forlængelse</h3>
      <p>
        Forlængelse betales typisk:
      </p>
      <ul>
        <li>Med samme dagspris som den originale booking</li>
        <li>Ved returnering sammen med eventuel rest</li>
        <li>Via den samme betalingsmetode I brugte før</li>
      </ul>
      <p>
        Nogle ejere giver rabat ved længere leje - det skader aldrig at spørge!
      </p>

      <h3>Hvis ejeren afviser</h3>
      <p>
        Værktøjet kan være booket af en anden, så:
      </p>
      <ul>
        <li>Returner til tiden som aftalt</li>
        <li>Spørg om du kan booke det igen senere</li>
        <li>Find et lignende værktøj til de resterende dage</li>
      </ul>

      <h3>Automatisk forlængelse</h3>
      <p>
        Nogle ejere tilbyder automatisk forlængelse hvis:
      </p>
      <ul>
        <li>Værktøjet ikke har andre bookinger</li>
        <li>Du har vist dig pålidelig</li>
        <li>Det er aftalt på forhånd</li>
      </ul>
      <p>
        Spørg din ejer om denne mulighed.
      </p>

      <h3>Forlængelse uden godkendelse</h3>
      <p>
        <strong>Vigtigt:</strong> Behold aldrig værktøjet længere uden ejerens accept!
      </p>
      <p>
        At overskride returdatoen uden aftale:
      </p>
      <ul>
        <li>Er respektløst overfor ejeren</li>
        <li>Kan påvirke din rating</li>
        <li>Kan medføre ekstra gebyrer</li>
        <li>Kan blokere værktøjet for andre</li>
      </ul>

      <h3>Gentagne forlængelser</h3>
      <p>
        Hvis du ofte forlænger dine lejer:
      </p>
      <ul>
        <li>Overvej at booke længere fra starten</li>
        <li>Diskutér ugetarif eller månedstarif med ejeren</li>
        <li>Det er ofte billigere end daglige satser</li>
      </ul>

      <h3>Annullér forlængelse</h3>
      <p>
        Hvis du fortryder forlængelsen:
      </p>
      <ul>
        <li>Informér ejeren så hurtigt som muligt</li>
        <li>Returner værktøjet som oprindeligt aftalt</li>
        <li>Vær respektfuld om ejerens planer</li>
      </ul>

      <h3>Tips til succesfuld forlængelse</h3>
      <ul>
        <li>Kommunikér tidligt og tydeligt</li>
        <li>Vær fleksibel med datoer</li>
        <li>Vis forståelse hvis det ikke er muligt</li>
        <li>Behandl værktøjet godt så ejeren er villig til at hjælpe</li>
        <li>Vær pålidelig med betaling og returnering</li>
      </ul>
    </>
  );

  return (
    <HelpArticle
      title="Forlæng din leje"
      category="Værktøjsleje"
      content={content}
      relatedArticles={[
        { title: 'Bookingprocessen forklaret', link: '/help/booking-process' },
        { title: 'Afbestillingspolitik', link: '/help/cancellation-policy' },
      ]}
    />
  );
};

export default ExtendRental;
